import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Phone, 
  ArrowRight, 
  Plus, 
  Minus, 
  X,
  Quote, 
  CheckCircle2, 
  Heart, 
  Users, 
  Sparkles, 
  FileText, 
  BookOpen, 
  Brain, 
  Activity, 
  Compass, 
  MessageSquare, 
  PauseCircle, 
  Target, 
  Droplets, 
  User, 
  Clock, 
  MapPin, 
  Laptop, 
  ChevronLeft, 
  ChevronRight,
  RefreshCw
} from 'lucide-react';

const SorgOgTab = () => {
  const [activeFaq, setActiveFaq] = useState(0); // Open first FAQ by default as in HTML
  const [currentSlide, setCurrentSlide] = useState(0);
  const trackRef = useRef(null);

  const canonicalUrl = "https://corehypnose.dk/sorg-og-tab";

  const slides = [
    {
      img: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sorg-tab-karusel-01.jpg",
      label: "1/5 · Tanke",
      alt: "Illustration af en naturlig tanke efter et tab."
    },
    {
      img: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sorg-tab-karusel-02.jpg",
      label: "2/5 · Grubleri",
      alt: "Illustration af hvordan sorgtanker kan køre i ring."
    },
    {
      img: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sorg-tab-karusel-03.jpg",
      label: "3/5 · Belastning",
      alt: "Illustration af hvordan grubleri kan belaste kroppen og hverdagen."
    },
    {
      img: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sorg-tab-karusel-04.jpg",
      label: "4/5 · Ny vej",
      alt: "Illustration af en mere fleksibel måde at forholde sig til sorgtanker på."
    },
    {
      img: "https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/sorg-tab-karusel-05.jpg",
      label: "5/5 · Mere plads omkring sorgen",
      alt: "Illustration af mere plads omkring sorgen i hverdagen."
    }
  ];

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    if (trackRef.current) {
      const step = Math.min(trackRef.current.clientWidth * 0.9, 460);
      trackRef.current.scrollBy({ left: -step, behavior: 'smooth' });
    }
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    if (trackRef.current) {
      const step = Math.min(trackRef.current.clientWidth * 0.9, 460);
      trackRef.current.scrollBy({ left: step, behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: "Er sorg noget, der skal behandles?",
      shortAnswer: "Nej",
      cards: [
        {
          icon: Heart,
          title: "Sorg er en naturlig reaktion",
          text: "Det gør ondt, fordi noget eller nogen har betydet noget."
        },
        {
          icon: Users,
          title: "Støtte kan være relevant",
          text: "Især hvis sorgen bliver meget tung, ensom, fastlåst eller styrer hverdagen."
        },
        {
          icon: Sparkles,
          title: "Målet er mere plads",
          text: "Ikke at fjerne sorgen, men at skabe mere rum omkring den."
        }
      ],
      summary: "Sorg er ikke forkert og skal ikke behandles væk. Du kan få støtte, når sorgen bliver svær at være i alene eller begynder at styre hverdagen.",
      links: [
        { label: "Book første session", path: "/bestil-tid" },
        { label: "Kontakt mig", path: "/kontakt" }
      ]
    },
    {
      question: "Hvad betyder sorgbearbejdning hos dig?",
      shortAnswer: "At arbejde nænsomt med det, der gør sorgen tungere",
      cards: [
        {
          icon: BookOpen,
          title: "Savn og minder",
          text: "Jeg kan hjælpe dig med at arbejde med minder og savn uden at presse noget væk."
        },
        {
          icon: Brain,
          title: "Tanker og grubleri",
          text: "Jeg undersøger sammen med dig, om tankerne kører i ring og gør belastningen større."
        },
        {
          icon: Activity,
          title: "Krop og hverdag",
          text: "Jeg ser på uro, træthed, søvn og det, der gør hverdagen svær."
        }
      ],
      summary: "Sorgbearbejdning betyder ikke, at sorgen skal fjernes. Det betyder, at jeg arbejder med savn, tanker, krop, minder og det, der kan gøre sorgen sværere at bære.",
      links: [
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
        { label: "Hypnose", path: "/hypnose" }
      ]
    },
    {
      question: "Skal jeg tale meget om det, der skete?",
      shortAnswer: "Kun i det omfang det giver mening",
      cards: [
        {
          icon: Compass,
          title: "Du bestemmer tempoet",
          text: "Du behøver ikke fortælle alt i detaljer."
        },
        {
          icon: MessageSquare,
          title: "Jeg taler med dig om det vigtigste",
          text: "Det kan være nok at begynde med det, der fylder lige nu."
        },
        {
          icon: PauseCircle,
          title: "Roligt og respektfuldt",
          text: "Jeg kan stoppe op, sænke tempoet eller skifte fokus undervejs."
        }
      ],
      summary: "Du skal ikke præstere en bestemt fortælling om tabet. Jeg kan også arbejde med, hvordan sorgen påvirker din hverdag nu.",
      links: [
        { label: "Om Lars", path: "/om-lars" },
        { label: "Første session", path: "/bestil-tid" }
      ]
    },
    {
      question: "Kan metakognitiv terapi bruges ved sorg?",
      shortAnswer: "Ja, når tankerne holder dig fast",
      cards: [
        {
          icon: Brain,
          title: "Ikke at tale sorgen væk",
          text: "Fokus er ikke at overbevise dig om, at sorgen ikke betyder noget."
        },
        {
          icon: RefreshCw,
          title: "Mindre mental rundkørsel",
          text: "Jeg ser på grubleri, selvbebrejdelse, bekymring og tankemylder."
        },
        {
          icon: Target,
          title: "Mere fleksibel opmærksomhed",
          text: "Minder og tanker kan være der uden konstant analyse."
        }
      ],
      summary: "Metakognitiv terapi kan være relevant, når sorg bliver forbundet med mange timers grubleri, skyld, bekymring eller mental kamp.",
      links: [
        { label: "Læs om metakognitiv terapi", path: "/metakognitiv-terapi" },
        { label: "Tankemylder", path: "/tankemylder" }
      ]
    },
    {
      question: "Kan hypnose hjælpe ved sorg og tab?",
      shortAnswer: "Det kan bruges som nænsom støtte",
      cards: [
        {
          icon: Target,
          title: "Rolig guidning",
          text: "Hypnose bruges som fokuseret opmærksomhed og rolig guidning."
        },
        {
          icon: Activity,
          title: "Støtte til kroppen",
          text: "Det kan støtte ro, nervesystem og indre belastning."
        },
        {
          icon: Heart,
          title: "Ikke at glemme",
          text: "Det handler ikke om at fjerne minder eller give slip på den, du har mistet."
        }
      ],
      summary: "Hypnose kan være en valgfri støtte til ro og indre billeder. Målet er mere ro og plads omkring det, der gør ondt.",
      links: [
        { label: "Læs om hypnose", path: "/hypnose" },
        { label: "Book første session", path: "/bestil-tid" }
      ]
    },
    {
      question: "Hvad hvis jeg græder i sessionen?",
      shortAnswer: "Så er det helt i orden",
      cards: [
        {
          icon: Droplets,
          title: "Du må gerne græde",
          text: "Du behøver ikke holde dig sammen."
        },
        {
          icon: User,
          title: "Jeg følger dig roligt",
          text: "Jeg tager det i et tempo, hvor du ikke skal præstere."
        },
        {
          icon: PauseCircle,
          title: "Der er plads til pauser",
          text: "Jeg kan stoppe op og justere undervejs."
        }
      ],
      summary: "Du behøver ikke komme med kontrol over følelserne. I sessionen er der plads til, at sorgen kan vise sig i et roligt og respektfuldt tempo.",
      links: [
        { label: "Book første session", path: "/bestil-tid" },
        { label: "Kontakt mig", path: "/kontakt" }
      ]
    },
    {
      question: "Kan jeg komme, selvom tabet ligger langt tilbage?",
      shortAnswer: "Ja",
      cards: [
        {
          icon: Clock,
          title: "Sorg har ikke en fast tidsplan",
          text: "Et gammelt tab kan fylde igen på nye tidspunkter."
        },
        {
          icon: FileText,
          title: "Noget kan blive aktiveret",
          text: "Minder, livsændringer eller nye tab kan vække sorgen på ny."
        },
        {
          icon: Plus,
          title: "Det kan stadig give mening",
          text: "Der kan stadig være brug for støtte og nye måder at være med sorgen på."
        }
      ],
      summary: "Sorg kan blive aktiveret eller fylde på nye måder, også længe efter et tab. Det er ikke for sent at få støtte.",
      links: [
        { label: "Depression", path: "/depression" },
        { label: "Stress", path: "/stress" }
      ]
    },
    {
      question: "Kan behandlingen foregå online?",
      shortAnswer: "Ja",
      cards: [
        {
          icon: MapPin,
          title: "I klinikken i Valby",
          text: "Mange vælger fysisk fremmøde, når sorgen føles meget personlig."
        },
        {
          icon: Laptop,
          title: "Online hjemmefra",
          text: "Online kan være roligt, hvis det er svært at komme ud."
        },
        {
          icon: Compass,
          title: "Du vælger rammen",
          text: "Jeg hjælper dig med at finde den form, der passer bedst til din situation."
        }
      ],
      summary: "Du kan vælge terapi i klinikken i Valby eller online. Begge dele er muligt.",
      links: [
        { label: "Læs om online sessioner", path: "/online-sessioner" },
        { label: "Book første session", path: "/bestil-tid" }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sorg og tab – terapi og sorgbearbejdning i Valby | Core Hypnose</title>
        <meta name="description" content="Få støtte ved sorg og tab hos Core Hypnose i Valby eller online. Nænsom terapi, sorgbearbejdning, metakognitiv terapi og hypnose ved savn, grubleri og uro." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Sorg og tab – terapi og sorgbearbejdning i Valby | Core Hypnose" />
        <meta property="og:description" content="Få støtte ved sorg og tab hos Core Hypnose i Valby eller online. Nænsom terapi, sorgbearbejdning, metakognitiv terapi og hypnose ved savn, grubleri og uro." />
        <meta property="og:image" content="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/rolig-stund-ved-vinduet.webp" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/rolig-stund-ved-vinduet.webp" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Er sorg noget, der skal behandles?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sorg er ikke forkert og skal ikke behandles væk. Du kan få støtte, når sorgen bliver svær at være i alene eller begynder at styre hverdagen."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad betyder sorgbearbejdning hos dig?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sorgbearbejdning betyder ikke, at sorgen skal fjernes. Det betyder, at jeg arbejder med savn, tanker, krop, minder og det, der kan gøre sorgen sværere at bære."
                }
              },
              {
                "@type": "Question",
                "name": "Skal jeg tale meget om det, der skete?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Du skal ikke præstere en bestemt fortælling om tabet. Jeg kan også arbejde med, hvordan sorgen påvirker din hverdag nu."
                }
              },
              {
                "@type": "Question",
                "name": "Kan metakognitiv terapi bruges ved sorg?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Metakognitiv terapi kan være relevant, når sorg bliver forbundet med mange timers grubleri, skyld, bekymring eller mental kamp."
                }
              },
              {
                "@type": "Question",
                "name": "Kan hypnose hjælpe ved sorg og tab?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnose kan være en valgfri støtte til ro og indre billeder. Målet er mere ro og plads omkring det, der gør ondt."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad hvis jeg græder i sessionen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Du behøver ikke komme med kontrol over følelserne. I sessionen er der plads til, at sorgen kan vise sig i et roligt og respektfuldt tempo."
                }
              },
              {
                "@type": "Question",
                "name": "Kan jeg komme, selvom tabet ligger langt tilbage?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sorg kan blive aktiveret eller fylde på nye måder, også længe efter et tab. Det er ikke for sent at få støtte."
                }
              },
              {
                "@type": "Question",
                "name": "Kan behandlingen foregå online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Du kan vælge terapi i klinikken i Valby eller online. Begge dele er muligt."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-[#FAF7F2] overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Copy */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Sorg og tab
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#123F13] leading-tight font-semibold">
                Når du har mistet nogen, og sorgen fylder
              </h1>
              <p className="text-lg md:text-xl text-[#3B352F]/80 leading-relaxed">
                Sorg skal ikke behandles væk. Men du kan få støtte, hvis savnet, tankerne, kroppen eller hverdagen bliver svær at være i. Jeg tilbyder nænsom terapi og sorgbearbejdning i Valby eller online.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link 
                  to="/bestil-tid" 
                  className="inline-flex items-center justify-center gap-2 bg-[#193f10] text-white px-6 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md"
                >
                  <Calendar size={18} /> Book første behandlingssession
                </Link>
                <a 
                  href="#saadan-arbejder-jeg" 
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#FAF7F2] transition-all"
                >
                  Se hvordan jeg arbejder
                </a>
              </div>
            </div>

            {/* Right Column: Visual Stack */}
            <div className="lg:col-span-5 space-y-8">
              <figure className="relative rounded-3xl overflow-hidden border border-[#E8DDCE] shadow-lg bg-[#FAF7F2]">
                <img 
                  alt="Kvinde sidder stille ved et vindue med kop te og notesbog som roligt billede på sorg og eftertanke." 
                  loading="eager" 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/rolig-stund-ved-vinduet.webp"
                  className="w-full h-auto object-cover aspect-[4/3] block"
                />
                <figcaption className="absolute bottom-4 left-4 right-4 p-4 rounded-2xl bg-white/85 border border-[#E8DDCE]/60 backdrop-blur-md shadow-sm">
                  <p className="font-serif text-[#123F13] font-bold text-sm">Sorg og tab</p>
                  <p className="text-xs text-[#3B352F]/60 mt-0.5">Et roligt sted at begynde</p>
                </figcaption>
              </figure>

              <div className="bg-white border border-[#E8DDCE] rounded-3xl p-6 md:p-8 shadow-sm relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-[#8C2F12]/5 pointer-events-none" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#3B352F]/50 mb-4">Det kan fylde efter et tab</h3>
                <ul className="space-y-3">
                  {[
                    "Savn, tomhed eller en følelse af uvirkelighed",
                    "Grubleri over det, der skete, eller det, der ikke blev sagt",
                    "Selvbebrejdelse, skyld eller mange “hvad nu hvis”-tanker",
                    "Kropslig uro, træthed, søvnproblemer eller indre alarm",
                    "Svært ved at vende tilbage til hverdagen"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-[#3B352F]/80">
                      <span aria-hidden="true" className="flex-shrink-0 w-5 h-5 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center text-xs font-bold">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section 2: Sorg er ikke forkert */}
      <section className="py-20 bg-white border-y border-[#E8DDCE]/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: Heading */}
            <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-32">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Sorg er ikke forkert
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
                Det gør ondt, fordi noget eller nogen har betydet noget
              </h2>
            </div>

            {/* Right Column: Text & Image */}
            <div className="lg:col-span-7 space-y-8">
              <div className="text-[#3B352F]/80 text-lg leading-relaxed space-y-6">
                <p>
                  Når du har mistet nogen, kan livet føles forandret. Nogle oplever en tung stilhed. Andre oplever uro, vrede, skyld, savn eller en følelse af at være langt væk fra sig selv.
                </p>
                <p>
                  For mange kommer sorgen i bølger. Den kan være stille den ene dag og meget stærk den næste. Det betyder ikke, at du gør noget forkert. Det betyder, at du står i noget, der betyder noget.
                </p>
                <div className="border-l-4 border-[#8C2F12] pl-6 py-2 my-8 bg-[#FAF7F2]/50 rounded-r-xl">
                  <p className="font-serif italic text-[#123F13] text-xl leading-relaxed">
                    Målet er ikke at glemme den, du har mistet. Målet er at få mere plads omkring sorgen, så den ikke skal fylde hele dit liv hele tiden.
                  </p>
                </div>
              </div>

              <figure className="relative rounded-3xl overflow-hidden border border-[#E8DDCE] shadow-md bg-[#FAF7F2]">
                <img 
                  alt="Sorg er ikke forkert - Lars Nissen Corell" 
                  loading="lazy" 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/lars-nissen-corell-portraet.webp"
                  className="w-full h-auto object-cover aspect-[4/3] block"
                />
                <figcaption className="absolute bottom-4 left-4 right-4 p-4 rounded-2xl bg-white/85 border border-[#E8DDCE]/60 backdrop-blur-md shadow-sm">
                  <p className="font-serif text-[#123F13] font-bold text-sm">Sorg er ikke forkert</p>
                  <p className="text-xs text-[#3B352F]/60 mt-0.5">Et stille rum med plads til det, der gør ondt</p>
                </figcaption>
              </figure>
            </div>

          </div>
        </div>
      </section>

      {/* Section 3: Når sorgen bliver fastlåst */}
      <section className="py-20 bg-[#FAF7F2]/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: Heading */}
            <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-32">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Når sorgen bliver fastlåst
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
                Nogle gange er det ikke sorgen alene, men alt det omkring den
              </h2>
            </div>

            {/* Right Column: Signals Grid */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { title: "Grubleri", desc: "Tankerne kører igen og igen om det, der skete." },
                  { title: "Skyld og selvbebrejdelse", desc: "Du leder efter det, du burde have gjort anderledes." },
                  { title: "Bekymring for fremtiden", desc: "Det kan føles svært at se, hvordan livet skal fortsætte." },
                  { title: "Undgåelse", desc: "Du undgår steder, minder, samtaler eller følelser, fordi det gør for ondt." },
                  { title: "Kropslig belastning", desc: "Sorg kan sætte sig som uro, træthed, spænding eller søvnproblemer." },
                  { title: "Ensomhed", desc: "Det kan være svært at forklare andre, hvordan det egentlig er." }
                ].map((signal, idx) => (
                  <div key={idx} className="bg-white border border-[#E8DDCE]/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="font-serif text-lg text-[#123F13] font-bold mb-2">{signal.title}</h3>
                    <p className="text-sm text-[#3B352F]/70 leading-relaxed">{signal.desc}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section 4: Sorgbearbejdning med ro og respekt */}
      <section className="py-20 bg-white border-y border-[#E8DDCE]/50" id="saadan-arbejder-jeg">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 items-end">
            <div className="lg:col-span-5 space-y-3">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Sorgbearbejdning med ro og respekt
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
                En nænsom måde at arbejde med sorg og tab
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-[#3B352F]/70 leading-relaxed">
                Jeg arbejder ikke med sorg som noget, der skal fjernes. Jeg arbejder med det, der kan gøre sorgen ekstra tung: grubleri, bekymring, selvbebrejdelse, indre alarm og fastlåst opmærksomhed.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Du skal ikke presse sorgen væk", desc: "Sorg må gerne være der. Jeg arbejder med, hvordan du kan være med den uden at blive opslugt af den hele tiden." },
              { title: "Mindre grubleri", desc: "Jeg undersøger sammen med dig, om tankerne er blevet noget, du bruger mange timer på at gennemgå igen og igen." },
              { title: "Mere ro i kroppen", desc: "Jeg kan hjælpe dig med at arbejde med kropslig uro, indre alarm, søvn og belastning i et roligt tempo." },
              { title: "Plads til minder", desc: "Målet er ikke at lukke ned for minderne, men at de kan være der uden at styre hele dagen." },
              { title: "Næste skridt", desc: "Jeg arbejder konkret med, hvordan hverdagen kan blive mere bæredygtig igen." },
              { title: "Fremmøde eller online", desc: "Mange foretrækker fysisk fremmøde ved sorg. Online kan også være en rolig mulighed." }
            ].map((card, idx) => (
              <div key={idx} className="bg-[#FAF7F2]/40 border border-[#E8DDCE]/60 rounded-2xl p-6 relative overflow-hidden group hover:bg-white hover:shadow-md transition-all duration-300">
                <div className="w-8 h-1 bg-[#8C2F12] rounded-full mb-4"></div>
                <h3 className="font-serif text-lg text-[#123F13] font-bold mb-3">{card.title}</h3>
                <p className="text-sm text-[#3B352F]/70 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Metakognitiv forståelse */}
      <section className="py-20 bg-[#FAF7F2]/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Column: Heading */}
            <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-32">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Metakognitiv forståelse
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
                Når tankerne bliver ved med at vende tilbage
              </h2>
              <p className="text-[#3B352F]/70 text-lg leading-relaxed">
                Det er naturligt at få tanker og minder efter et tab. Men nogle gange bliver man fanget i at forsøge at løse det, der ikke kan løses med tanker alene.
              </p>
            </div>

            {/* Right Column: Process Steps */}
            <div className="lg:col-span-7 space-y-6">
              <div className="relative pl-8 border-l-2 border-[#E8DDCE] space-y-8">
                {[
                  { step: "Tanke", title: "“Kunne jeg have gjort noget anderledes?”", desc: "Tanken kan føles vigtig og kræve et svar." },
                  { step: "Grubleri", title: "Du gennemgår situationen igen og igen", desc: "Det kan føles som sorgbearbejdning, men nogle gange bliver det en mental rundkørsel." },
                  { step: "Belastning", title: "Kroppen og hverdagen bliver tungere", desc: "Søvnen, energien, nærværet og overskuddet kan blive påvirket." },
                  { step: "Ny vej", title: "Du lærer at lade tanker være tanker", desc: "Ikke fordi de er ligegyldige, men fordi du ikke behøver følge dem hele dagen for at ære den, du har mistet." }
                ].map((item, idx) => (
                  <div key={idx} className="relative">
                    {/* Circle marker */}
                    <div className="absolute -left-[45px] top-0.5 w-7 h-7 rounded-full bg-white border-2 border-[#8C2F12] text-[#8C2F12] flex items-center justify-center text-xs font-bold shadow-sm">
                      {idx + 1}
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#8C2F12] bg-[#8C2F12]/5 px-2 py-0.5 rounded-full">{item.step}</span>
                      <h3 className="font-serif text-lg text-[#123F13] font-bold pt-1">{item.title}</h3>
                      <p className="text-sm text-[#3B352F]/70 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Carousel Block */}
          <div className="bg-white border border-[#E8DDCE] rounded-3xl p-6 md:p-8 shadow-md mt-16">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-6">
              <div>
                <h3 className="font-serif text-xl md:text-2xl text-[#123F13] font-bold">Illustrationer af metakognitiv sorgbearbejdning</h3>
                <p className="text-sm text-[#3B352F]/60 mt-1">5 illustrationer: fra tanker og grubleri til mere plads omkring sorgen</p>
              </div>
              <div className="flex gap-2 self-end sm:self-auto">
                <button 
                  onClick={handlePrevSlide}
                  className="w-10 h-10 rounded-full border border-[#8C2F12]/30 bg-white text-[#123F13] hover:border-[#8C2F12] hover:text-[#8C2F12] flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Forrige billede"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  onClick={handleNextSlide}
                  className="w-10 h-10 rounded-full border border-[#8C2F12]/30 bg-white text-[#123F13] hover:border-[#8C2F12] hover:text-[#8C2F12] flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Næste billede"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            {/* Carousel Track */}
            <div 
              ref={trackRef}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 scrollbar-thin scrollbar-thumb-[#E8DDCE] scrollbar-track-transparent"
            >
              {slides.map((slide, idx) => (
                <figure 
                  key={idx} 
                  className="flex-shrink-0 w-[280px] sm:w-[360px] snap-center bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-2xl overflow-hidden shadow-sm"
                >
                  <img 
                    src={slide.img} 
                    alt={slide.alt} 
                    loading="lazy"
                    className="w-full h-auto aspect-square object-contain bg-white block"
                  />
                  <figcaption className="p-4 bg-white border-t border-[#E8DDCE]/40 font-serif text-sm font-bold text-[#123F13] text-center">
                    {slide.label}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Section 6: Core Hypnoses samlede metode */}
      <section className="py-20 bg-white border-y border-[#E8DDCE]/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              Core Hypnoses samlede metode
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
              Metakognitiv terapi og hypnose ved sorg
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Metakognitiv Terapi */}
            <div className="bg-[#FAF7F2]/40 border border-[#E8DDCE]/60 rounded-3xl p-8 space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Metakognitiv terapi
              </span>
              <h3 className="text-2xl font-serif text-[#123F13] font-bold">Mindre fastlåsning i tanker, skyld og bekymring</h3>
              <p className="text-[#3B352F]/80 leading-relaxed">
                Metakognitiv terapi kan hjælpe, når sorg bliver forbundet med mange timers grubleri, selvbebrejdelse, bekymring eller mental kamp. Fokus er ikke at diskutere sorgen væk, men at ændre forholdet til de tanker, der holder dig fast.
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  "mindre tid brugt på grubleri og selvbebrejdelse",
                  "mere fleksibel opmærksomhed",
                  "minder og tanker kan være der uden konstant analyse",
                  "konkrete skridt tilbage mod hverdagen"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-[#3B352F]/80">
                    <span className="text-[#8C2F12] select-none shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#8C2F12]" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hypnose */}
            <div className="bg-[#FAF7F2]/40 border border-[#E8DDCE]/60 rounded-3xl p-8 space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Hypnose
              </span>
              <h3 className="text-2xl font-serif text-[#123F13] font-bold">Rolig støtte til kroppen, savnet og de indre billeder</h3>
              <p className="text-[#3B352F]/80 leading-relaxed">
                Hypnose kan bruges nænsomt som støtte til ro, kropslig regulering og indre billeder. Det handler ikke om at glemme eller give slip på den, du har mistet. Det handler om at skabe mere ro og plads omkring det, der gør ondt.
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  "rolig guidning uden mystik",
                  "støtte til kroppen og nervesystemet",
                  "nænsomt arbejde med indre billeder og minder",
                  "altid i et tempo, der føles respektfuldt"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-[#3B352F]/80">
                    <span className="text-[#8C2F12] select-none shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#8C2F12]" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Section 7: Første behandlingssession */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-[#123F13] text-white rounded-3xl p-8 md:p-12 shadow-xl relative overflow-hidden">
            <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Copy */}
              <div className="lg:col-span-7 space-y-6">
                <span className="text-[#E8DDCE] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                  <span className="w-8 h-[1px] bg-[#E8DDCE]"></span>
                  Første behandlingssession
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-tight font-semibold break-words">
                  Første behandlingssession i Valby eller online
                </h2>
                <p className="text-white/80 text-lg leading-relaxed">
                  Den første session handler om at skabe overblik. Jeg taler med dig om, hvad du har mistet, hvordan sorgen viser sig, og hvad der især er svært lige nu. Du behøver ikke vide præcis, hvad du har brug for, før du kommer.
                </p>

                {/* Meta Items */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
                  {[
                    { title: "375 kr.", subtitle: "første session" },
                    { title: "50 minutter", subtitle: "personlig afklaring" },
                    { title: "Valby eller online", subtitle: "du vælger rammen" }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white/10 border border-white/15 rounded-2xl p-4 text-center">
                      <p className="text-lg md:text-xl font-bold text-white">{item.title}</p>
                      <p className="text-xs text-white/70 mt-0.5">{item.subtitle}</p>
                    </div>
                  ))}
                </div>

                {/* Points */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/90">
                  {[
                    "Personlig kortlægning af sorg, savn og belastning",
                    "Afklaring af, om sorgbearbejdning eller et forløb giver mening",
                    "Rolige øvelser og konkrete næste skridt",
                    "En tydelig plan for, hvad der kan hjælpe dig videre"
                  ].map((point, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D78373] mt-2 flex-shrink-0"></span>
                      <span>{point}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-white/60 italic pt-2">Betaling sker altid inden sessionen starter.</p>

                <div className="pt-4">
                  <Link 
                    to="/bestil-tid" 
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#123F13] px-6 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#FAF7F2] transition-all shadow-md"
                  >
                    Book første behandlingssession
                  </Link>
                </div>
              </div>

              {/* Right Column: Photo */}
              <div className="lg:col-span-5">
                <figure className="relative rounded-3xl overflow-hidden border border-white/10 shadow-lg bg-white/5">
                  <img 
                    alt="Lars Nissen Corell – Psykoterapeut MPF i et roligt terapirum" 
                    loading="lazy" 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/lars-nissen-corell-portraet.jpg"
                    className="w-full h-auto object-cover aspect-[2/3] block"
                  />
                  <figcaption className="absolute bottom-4 left-4 right-4 p-4 rounded-2xl bg-white/90 text-[#123F13] shadow-sm">
                    <p className="font-serif font-bold text-sm">Første behandlingssession</p>
                    <p className="text-xs text-[#3B352F]/60 mt-0.5">Rolig afklaring i Valby eller online</p>
                  </figcaption>
                </figure>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Fremmøde eller online */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-white py-16 md:py-24 border-y border-[#E8DDCE] px-6 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]"></span>
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Fremmøde eller online</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight">
              Terapi ved sorg i Valby eller online
            </h2>
            <p className="text-[#3B352F]/80 text-lg leading-relaxed">
              Mange oplever, at fysisk fremmøde føles mere ægte og trygt, når de står i sorg. Andre har mest ro ved at sidde hjemme. Begge dele er muligt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* I klinikken i Valby */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] p-8 rounded-3xl flex flex-col justify-between hover:shadow-md transition-all">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-[#123F13] mb-6 shadow-sm border border-[#E8DDCE]/50">
                  <MapPin size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">I klinikken i Valby</h3>
                <p className="text-[#3B352F]/70 leading-relaxed mb-6">
                  Core Hypnose ligger på Valby Tingsted 7, 2500 Valby. Her kan du møde fysisk op i rolige rammer.
                </p>
              </div>
              <div>
                <Link to="/kontakt" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
                  Se kontakt og praktisk info <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Online session */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] p-8 rounded-3xl flex flex-col justify-between hover:shadow-md transition-all">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-[#123F13] mb-6 shadow-sm border border-[#E8DDCE]/50">
                  <Laptop size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">Online session</h3>
                <p className="text-[#3B352F]/70 leading-relaxed mb-6">
                  Online kan være relevant, hvis det er svært at komme ud, eller hvis du har brug for at sidde i egne omgivelser.
                </p>
              </div>
              <div>
                <Link to="/online-sessioner" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
                  Læs om online sessioner <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: FAQ */}
      <section className="py-20 bg-[#FAF7F2] border-t border-[#E8DDCE]/60" id="faq">
        <div className="max-w-6xl mx-auto px-6">
          
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#123F13] leading-tight font-semibold">
              Ofte stillede spørgsmål
            </h2>
            <p className="text-lg text-[#3B352F]/70 leading-relaxed">
              Åbn et spørgsmål og få svaret forklaret visuelt, roligt og konkret.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {faqItems.map((item, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <div 
                  key={idx} 
                  className={`bg-white border rounded-3xl overflow-hidden transition-all duration-300 shadow-sm flex flex-col h-full ${
                    isOpen ? 'border-[#CDBAA0] shadow-md' : 'border-[#D2C5B3]/95'
                  }`}
                >
                  {/* Summary/Header */}
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full p-4 sm:p-6 flex justify-between items-center text-left group cursor-pointer"
                    aria-expanded={isOpen}
                  >
                    <h3 className="text-lg font-bold text-[#123F13] pr-8">{item.question}</h3>
                    <div className="text-[#8C2F12] transition-transform duration-300 shrink-0">
                      {isOpen ? <X size={20} /> : <Plus size={20} />}
                    </div>
                  </button>

                  {/* Body */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-[#E8DDCE]/80 bg-gradient-to-b from-[#FAF7F2]/30 to-[#FAF7F2]/70">
                          
                          {/* Visual Answer Box */}
                          <div className="p-6 md:p-8 space-y-6">
                            
                            {/* Short Answer Box */}
                            <div className="border border-[#D8C5AA] rounded-2xl p-6 bg-gradient-to-br from-white to-[#F7EDDF] text-center shadow-inner relative overflow-hidden">
                              <div className="absolute -right-6 -top-6 w-16 h-16 rounded-full bg-[#8C2F12]/5 pointer-events-none" />
                              <span className="block text-[10px] font-bold tracking-widest text-[#8A8177] uppercase mb-1">Kort svar</span>
                              <strong className="block font-serif text-2xl md:text-3xl lg:text-4xl text-[#8C2F12] font-semibold tracking-tight leading-tight">
                                {item.shortAnswer}
                              </strong>
                            </div>

                            {/* Cards Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {item.cards.map((card, cIdx) => {
                                const CardIcon = card.icon;
                                return (
                                  <div key={cIdx} className="bg-white border border-[#DAC9B0] rounded-2xl p-5 text-center flex flex-col items-center justify-between shadow-sm">
                                    <div className="w-12 h-12 rounded-full border border-[#D7C3A6] bg-[#FBF6EF] text-[#123F13] flex items-center justify-center mb-4">
                                      <CardIcon size={22} />
                                    </div>
                                    <div className="w-6 h-[2px] bg-[#8C2F12] mb-3 rounded-full"></div>
                                    <h4 className="font-serif text-base text-[#123F13] font-bold mb-1.5 leading-snug">{card.title}</h4>
                                    <p className="text-xs text-[#3B352F]/70 leading-relaxed mt-auto">{card.text}</p>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Summary Box */}
                            <div className="flex gap-4 items-center bg-[#F7F1E8]/80 border border-[#D5C0A1] rounded-2xl p-5">
                              <div className="w-10 h-10 rounded-full bg-white text-[#123F13] flex items-center justify-center flex-shrink-0 shadow-sm">
                                <CheckCircle2 size={20} className="text-[#123F13]" />
                              </div>
                              <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                                {item.summary}
                              </p>
                            </div>

                            {/* Links Footer */}
                            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E8DDCE]/60">
                              <span className="text-[10px] font-bold tracking-wider text-[#7A6E65] uppercase">Læs videre</span>
                              {item.links.map((link, lIdx) => (
                                <Link 
                                  key={lIdx} 
                                  to={link.path} 
                                  className="inline-flex items-center gap-1 text-xs font-bold text-[#123F13] bg-white border border-[#8C2F12]/20 hover:border-[#8C2F12] px-3.5 py-2 rounded-full transition-colors"
                                >
                                  {link.label} <ArrowRight size={12} className="text-[#8C2F12]" />
                                </Link>
                              ))}
                            </div>

                          </div>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Section 10: Relevant Pages */}
      <section className="py-20 bg-white border-t border-[#E8DDCE]/80">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              Læs også
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight font-semibold">
              Relevante sider
            </h2>
            <p className="text-[#3B352F]/70 leading-relaxed">
              Her kan du læse mere om områder, der ofte hænger sammen med sorg, savn og tab.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Depression", path: "/depression" },
              { label: "Stress", path: "/stress" },
              { label: "Tankemylder", path: "/tankemylder" },
              { label: "Søvnproblemer", path: "/soevnproblemer" },
              { label: "Angst", path: "/angst" },
              { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
              { label: "Hypnose", path: "/hypnose" },
              { label: "Online sessioner", path: "/online-sessioner" }
            ].map((item, idx) => (
              <Link 
                key={idx} 
                to={item.path} 
                className="flex items-center justify-between p-4 bg-white border border-[#D2C5B3]/95 rounded-xl text-sm font-bold text-[#123F13] hover:border-[#CBB9A1] hover:shadow-sm hover:-translate-y-0.5 transition-all"
              >
                <span>{item.label}</span>
                <span className="text-[#8C2F12] font-bold">›</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section 11: Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#123F13] to-[#193f10] text-white relative overflow-hidden">
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-white/[0.03] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 space-y-6 flex flex-col items-center">
          <div className="mb-2">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
              alt="Core Kvalitetsstempel" 
              className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain mx-auto"
            />
          </div>
          <span className="text-[#FAF7F2]/80 font-bold text-xs uppercase tracking-widest block">
            Et nænsomt første skridt
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-[#FAF7F2] font-semibold leading-tight max-w-3xl mx-auto">
            Du behøver ikke stå alene med sorgen
          </h2>
          <p className="text-lg text-[#FAF7F2]/80 max-w-2xl mx-auto leading-relaxed">
            Du behøver ikke vide præcis, hvad du skal sige, før du kommer. Jeg kan begynde med en rolig samtale om, hvad du står i, og hvad der kan hjælpe dig videre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 w-full">
            <Link 
              to="/bestil-tid" 
              className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
            >
              Book første behandlingssession
            </Link>
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center border-2 border-[#FAF7F2] text-[#FAF7F2] px-8 py-4 rounded-lg font-bold text-base hover:bg-white/10 transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default SorgOgTab;
